/**
 * Tenká vrstva nad fetch — spoločná pre portál aj všetky hry. Session drží
 * httpOnly cookie, preto všade `credentials: 'include'` — v prehliadači sa
 * k tokenu nedá dostať z JS.
 *
 * Tu je len to, čo patrí portálu (HTTP, chyby, účet). API konkrétnej hry je
 * v `games/<hra>/api.ts`.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Chyba ${response.status}`;
    throw new ApiError(message, response.status);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
};

// --- typy odpovedí ---------------------------------------------------------

export interface SessionUser {
  id: number;
  nick: string;
}

/** Stav hry tak, ako ho vidí jeden hráč — súperova ruka je pole nulových hodnôt. */

export const auth = {
  me: () => api.get<{ user: SessionUser }>('/auth/me'),
  /** Registrácia na portál: kód z pozvánky sa spotrebuje, ďalej stačí meno a heslo. */
  register: (code: string, nick: string, password: string) =>
    api.post<{ user: SessionUser; claimed: boolean }>('/auth/register', { code, nick, password }),
  login: (nick: string, password: string) =>
    api.post<{ user: SessionUser }>('/auth/login', { nick, password }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
};
